<script setup>
import { useStore } from 'vuex'
import LineTable from '@/components/water-line/LineTable.vue'
import PhreaticLineChart from '@/components/phreaticline/PhreaticLineChart.vue'
import MultiplePhreaticLineTable from '@/components/phreaticline/MultiplePhreaticLineTable.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import TabTemplate from '@/components/common/TabTemplate.vue'
import ActionButton from '@/components/common/ActionButton.vue'
import PhreaticLineCopyDialog from '@/components/phreaticline/PhreaticLineCopyDialog.vue'
import PhreaticLineDryTornCutDialog from '@/components/phreaticline/PhreaticLineDryTornCutDialog.vue'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useHeightComposable } from '@/composables/useHeightComposable'

const store = useStore()

const loading = ref(false)
const hoverPoint = ref({})

const topHeight = inject('topHeight')

const tableContainer = ref(null)
const tableContainerHeight = ref(0)

const { observerWatcher, unmountObserver } = useHeightComposable(
  tableContainerHeight,
  tableContainer
)

const reduceHeight = computed(() => {
  return topHeight?.value + tableContainerHeight.value + 'px'
})

const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)

const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHavePhreaticLine']
)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const currentCompositionId = computed(
  () => store.getters['stabSchematisationStage/currentCompositionId']
)

watch(currentStabSchematisationStage, async () => {
  hoverPoint.value = {}
  deleteActiveItem()
  if (currentStabSchematisationStage.value) {
    await resetState()
    await fetchItems()
  }
})

const toggleCopyDialog = () => store.dispatch('phreaticReferenceLine/toggleCopyDialog')

const fetchSoilLayerItems = async (id) => store.dispatch('soilLayer/fetchItems', id)

// Multiple phreatic line
const multiplePhreaticLine = ref(null)

const currentMultiplePhreaticLine = computed(() => store.state.multiplePhreaticLine.currentItem)
const importLoading = computed(() => store.state.multiplePhreaticLine.importLoading)
const importDialog = computed(() => store.state.multiplePhreaticLine.importDialog)

watch(() => currentMultiplePhreaticLine.value, observerWatcher)

const fetchMultiplePhreaticLineItems = async (id) =>
  store.dispatch('multiplePhreaticLine/fetchItems', id)

const resetStateMultiplePhreaticLine = async () => store.dispatch('multiplePhreaticLine/resetState')

const toggleImportDialog = (value) =>
  store.dispatch('multiplePhreaticLine/toggleImportDialog', value)
const importPhreaticLineFromStage = async (payload) =>
  store.dispatch('multiplePhreaticLine/importPhreaticLineFromStage', payload)

// Phreatic line

const phreaticLineChart = ref(null)

const currentItem = computed(() => store.state.phreaticLine.currentItem)
const cutDryTornZoneLoading = computed(() => store.state.phreaticLine.cutDryTornZoneLoading)
const resetStatePhreaticLine = async () => store.dispatch('phreaticLine/resetState')
const fetchPhreaticLineItems = async (id) => store.dispatch('phreaticLine/fetchItem', id)

const generatePhreaticLine = async () => store.dispatch('phreaticLine/generate')

const cutDryTornZone = async () => store.dispatch('phreaticLine/cutDryTornZone')

const toggleDryTornZoneDialog = () => store.dispatch('phreaticLine/toggleDryTornZoneDialog')

const generatePhreaticLineHandler = async () => {
  const isGenerated = await generatePhreaticLine()
  if (isGenerated) {
    multiplePhreaticLine.value.dirtyItems = []
  }
}
const cutDryTornZoneHandler = async () => {
  const isCut = await cutDryTornZone()
  if (isCut) {
    multiplePhreaticLine.value.dirtyItems = []
  }
  toggleDryTornZoneDialog()
}
const handleImport = async (sourceSchematisationStageId) => {
  try {
    deleteActiveItem()
    await importPhreaticLineFromStage({
      sourceSchematisationStageId,
      targetSchematisationStageId: currentStabSchematisationStage.value.id
    })
  } catch (e) {
    console.log(e)
  } finally {
    toggleImportDialog(false)
  }
}

// Phreatic reference line
const phreaticReferenceLine = ref(null)

const copyLoading = computed(() => store.state.phreaticReferenceLine.copyLoading)

const resetStatePhreaticReferenceLine = async () =>
  store.dispatch('phreaticReferenceLine/resetState')

const fetchPhreaticReferenceLineItems = async (id) =>
  store.dispatch('phreaticReferenceLine/fetchItem', id)

const generatePhreaticReferenceLine = async () => store.dispatch('phreaticReferenceLine/generate')

const copyClick = () => {
  phreaticReferenceLine.value.dirtyItems = []
}

const generateAndFetchSoilLayer = async () => {
  await generatePhreaticReferenceLine()
  await fetchSoilLayerItems(currentCompositionId.value)
}
const resetState = async () => {
  await resetStateMultiplePhreaticLine()
  await resetStatePhreaticLine()
  await resetStatePhreaticReferenceLine()
}

const fetchItems = async () => {
  await fetchMultiplePhreaticLineItems(currentStabSchematisationStage.value.id)
  await fetchPhreaticLineItems(currentMultiplePhreaticLine.value['phreaticline'])
  await fetchPhreaticReferenceLineItems(currentMultiplePhreaticLine.value['phreaticreferenceline'])
}

const setActiveItem = (index, storeNamespace) => {
  phreaticLineChart.value.waterLineChart.setActiveItem({
    storeNamespace: storeNamespace,
    index: index
  })
}
const deleteActiveItem = () => {
  phreaticLineChart.value.waterLineChart.deleteActiveItem()
}
const getHoverPoint = (point) => {
  hoverPoint.value = point
}

onMounted(async () => {
  loading.value = true
  await resetState()
  try {
    await fetchItems()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
})

onUnmounted(() => {
  unmountObserver()
})
</script>

<template>
  <TabTemplate>
    <template v-slot:chart>
      <PhreaticLineChart ref="phreaticLineChart" @clickPoint="getHoverPoint"></PhreaticLineChart>
    </template>
    <template v-slot:content>
      <v-lazy>
        <div class="d-flex fill-height flex-column">
          <v-container ref="tableContainer">
            <MultiplePhreaticLineTable
              v-if="currentMultiplePhreaticLine"
              ref="multiplePhreaticLine"
            />
            <div v-if="currentItem && currentItem.properties.soil_is_cut" class="pa-3">
              <v-alert density="compact" type="warning">
                The soil composition is cut, changes made in the latest stage with soil profiles or
                topo profile will not reflect in the current stage. To change it to the latest
                stage, re-cut the by saving the phreatic line.
              </v-alert>
            </div>
          </v-container>
          <v-row no-gutters>
            <v-col class="fill-height pa-0">
              <LineTable
                text="Phreatic line"
                storeNamespace="phreaticLine"
                :hoverPoint="hoverPoint"
                :generateButton="true"
                :readonly="isLocked"
                :reduce-height="reduceHeight"
                @setActiveItem="setActiveItem($event, 'phreaticLine')"
                @generate="generatePhreaticLineHandler()"
              >
                <template v-slot:left>
                  <ActionButton
                    :disabled="isLocked"
                    class="mr-2"
                    :loading="cutDryTornZoneLoading"
                    @action="toggleDryTornZoneDialog()"
                  >
                    Dry torn
                  </ActionButton>
                </template>
              </LineTable>
            </v-col>
            <v-col class="fill-height pa-0">
              <LineTable
                text="Phreatic reference line"
                storeNamespace="phreaticReferenceLine"
                ref="phreaticReferenceLine"
                :hoverPoint="hoverPoint"
                :generateButton="true"
                :readonly="isLocked"
                :reduce-height="reduceHeight"
                @setActiveItem="setActiveItem($event, 'phreaticReferenceLine')"
                @generate="generateAndFetchSoilLayer()"
              >
                <template v-slot:left>
                  <ActionButton
                    :disabled="isLocked"
                    class="mr-2"
                    :loading="copyLoading"
                    @action="toggleCopyDialog()"
                  >
                    Copy
                  </ActionButton>
                </template>
                >
              </LineTable>
            </v-col>
          </v-row>
          <ImportFromPreviousStageDialog
            :schematisation-stages="schematisationStagesToImport"
            v-model="importDialog"
            :loading="importLoading"
            text="phreatic lines"
            @close="toggleImportDialog(false)"
            @import="handleImport"
          />
          <PhreaticLineCopyDialog @copyClick="copyClick()"></PhreaticLineCopyDialog>
          <PhreaticLineDryTornCutDialog
            @on-cut="cutDryTornZoneHandler"
            @toggle="toggleDryTornZoneDialog"
          ></PhreaticLineDryTornCutDialog>
        </div>
      </v-lazy>
    </template>
  </TabTemplate>
</template>

<style scoped>
.v-container {
  padding: 0;
  margin: 0;
  min-width: 100%;
}
</style>
